// OpenLayers
import VectorImageLayer from "ol/layer/VectorImage.js";
import OlVectorSource from "ol/source/Vector";
import GeoJSON from "ol/format/GeoJSON.js";
import { createEmpty, extend } from "ol/extent";

// Utils
import { reprojectFeature } from "utils/OpenLayers/TransformUtils";
import {
  olAddLayerToGroup,
  removeOLLayerByName
} from "utils/OpenLayers/OLLayerUtils";
import { OLMapStyles } from "utils/OpenLayers/OLMapStyles";
import { DEFAULT_COLOR } from "assets/global";

/**
 * Adds a solar site to the OpenLayers map as a vector polygon layer
 *
 * @param {Object} map - The OpenLayers map instance from Map2dContext
 * @param {Object} site - The site object containing area geometry and metadata
 * @param {String} color - The color for the site polygon (rgba format with opacity)
 * @param {Number} scenarioId - The scenario ID for layer naming
 *
 * @returns {void}
 */
export const olAddSite = (map, site, color = DEFAULT_COLOR, scenarioId) => {
  const MapStylesObject = new OLMapStyles();

  // Validate that the site has area geometry
  if (!site?.area || !site?.area?.coordinates) {
    console.warn(`Site ${site?.id} has no area geometry to display`);
    return;
  }

  // Create a GeoJSON feature from the site area
  let siteFeature;
  try {
    siteFeature = new GeoJSON().readFeature({
      type: "Feature",
      geometry: site.area,
      properties: {
        id: site.id,
        name: site.name,
        rank: site.rank,
        scenario: site.scenario
      }
    });

    // Reproject the feature to the map projection (EPSG:3857)
    reprojectFeature(siteFeature);
  } catch (error) {
    console.error(`Error creating feature for site ${site?.id}:`, error);
    return;
  }

  // Generate the layer name using scenario + site ID pattern
  const siteLayerName = parseInt("".concat(scenarioId, site.id));

  // Remove existing layer if it exists
  removeOLLayerByName(map, siteLayerName);

  // Generate the style for the site
  const siteStyle = MapStylesObject.genSiteStyle(color);

  // Create the vector layer
  const vectorLayer = new VectorImageLayer({
    name: siteLayerName,
    source: new OlVectorSource({
      features: [siteFeature]
    }),
    style: siteStyle,
    layerType: "SitesLayer"
  });

  // Set custom properties for identification
  vectorLayer.set("id", site.id);
  vectorLayer.set("scenarioId", scenarioId);
  vectorLayer.set("siteId", site.id);
  vectorLayer.set("type", "site");

  // Add the layer to the sites group
  olAddLayerToGroup(map, "sitesGroup", vectorLayer);
};

/**
 * Removes a solar site layer from the OpenLayers map
 *
 * @param {Object} map - The OpenLayers map instance from Map2dContext
 * @param {Number} siteId - The ID of the site to remove
 * @param {Number} scenarioId - The scenario ID for layer naming
 *
 * @returns {void}
 */
export const olRemoveSite = (map, siteId, scenarioId) => {
  const siteLayerName = parseInt("".concat(scenarioId, siteId));
  removeOLLayerByName(map, siteLayerName);
};

/**
 * Updates the color of an existing site layer on the OpenLayers map
 * This method updates the style without recreating the entire layer
 *
 * @param {Object} map - The OpenLayers map instance from Map2dContext
 * @param {Number} siteId - The ID of the site to update
 * @param {Number} scenarioId - The scenario ID for layer naming
 * @param {String} color - The new color in rgba format (e.g., "rgba(18, 153, 236, 0.6)")
 *
 * @returns {void}
 */
export const olSetSiteColor = (map, siteId, scenarioId, color) => {
  const MapStylesObject = new OLMapStyles();
  const siteLayerName = parseInt("".concat(scenarioId, siteId));

  // Find all layers in the map
  const layers = map.getLayers().getArray();

  // Search through all layer groups to find the sites group
  for (const layer of layers) {
    if (layer.get("name") === "sitesGroup" && layer.getLayers) {
      const siteLayers = layer.getLayers().getArray();

      // Find the specific site layer
      const siteLayer = siteLayers.find(l => l.get("name") === siteLayerName);

      if (siteLayer) {
        // Generate new style with the updated color
        const newStyle = MapStylesObject.genSiteStyle(color);

        // Apply the new style to the layer
        siteLayer.setStyle(newStyle);

        // Force the layer to refresh
        siteLayer.changed();

        return;
      }
    }
  }

  console.warn(`Site layer ${siteLayerName} not found for color update`);
};

/**
 * Zooms the map view to fit all the provided sites' extents
 *
 * @param {Object} map - The OpenLayers map instance from Map2dContext
 * @param {Array} sites - Array of site objects containing area geometries
 * @param {Object} options - Optional configuration for the zoom animation
 * @param {Array} options.padding - Padding around the extent [top, right, bottom, left] (default: [50, 50, 50, 50])
 * @param {Number} options.duration - Animation duration in milliseconds (default: 1000)
 * @param {Number} options.maxZoom - Maximum zoom level (default: 18)
 *
 * @returns {Boolean} - Returns true if zoom was successful, false otherwise
 */
export const olZoomToSitesExtent = (map, sites, options = {}) => {
  const { padding = [50, 50, 50, 50], duration = 1000, maxZoom = 18 } = options;

  // Validate input
  if (!map || !sites || sites.length === 0) {
    console.warn("Invalid map or sites array provided to olZoomToSitesExtent");
    return false;
  }

  try {
    // Create an empty extent to accumulate all site extents
    const combinedExtent = createEmpty();
    let hasValidSites = false;

    // Iterate through all sites and extend the combined extent
    for (const site of sites) {
      if (site?.area && site?.area?.coordinates) {
        try {
          // Create a GeoJSON feature from the site area
          const siteFeature = new GeoJSON().readFeature({
            type: "Feature",
            geometry: site.area
          });

          // Reproject the feature to the map projection (EPSG:3857)
          reprojectFeature(siteFeature);

          // Get the extent of this feature and extend the combined extent
          const featureExtent = siteFeature.getGeometry().getExtent();
          extend(combinedExtent, featureExtent);
          hasValidSites = true;
        } catch (featureError) {
          console.warn(
            `Error processing site ${site.id} for extent calculation:`,
            featureError
          );
        }
      }
    }

    // Fit the map view to the combined extent if we have valid sites
    if (hasValidSites && combinedExtent[0] !== Infinity) {
      map.getView().fit(combinedExtent, {
        padding,
        duration,
        maxZoom
      });
      return true;
    } else {
      console.warn("No valid site geometries found for zoom extent");
      return false;
    }
  } catch (error) {
    console.error("Error zooming to sites extent:", error);
    return false;
  }
};
